/**
 * SPECTREIQ Landing Page
 *
 * WHY: Public-facing landing page with "Tactical Minimalism" aesthetic for DoD users.
 * Features architecture visualization, hero section, and seamless auth integration.
 *
 * Auth Safety: This component checks isAuthenticated and isLoading states from the
 * existing auth context. It does NOT modify any auth flow - it simply redirects
 * authenticated users to /dashboard. The OAuth callback still happens on /login.
 */

import { useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '@/hooks/useAuth'
import { useTheme, toggleTheme } from '@/hooks/useTheme'
import { Button } from '@/components/ui/button'
import { SpectreArchitecture } from '@/components/landing/SpectreArchitecture'
import { Sun, Moon, Rocket } from 'lucide-react'

export function LandingPage() {
  const { isAuthenticated, isLoading, login } = useAuth()
  const navigate = useNavigate()
  const theme = useTheme()
  const isDark = theme === 'dark'

  // Redirect authenticated users to dashboard
  useEffect(() => {
    if (!isLoading && isAuthenticated) {
      navigate('/dashboard', { replace: true })
    }
  }, [isAuthenticated, isLoading, navigate])

  // Show loading spinner while auth state initializes
  if (isLoading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 border-4 border-[#e26e32] border-t-transparent rounded-full animate-spin" />
          <p className="text-white/60 text-sm tracking-wider uppercase">Initializing...</p>
        </div>
      </div>
    )
  }

  // Theme-aware wordmark paths
  const wordmark = isDark
    ? '/WORDMARK_SPECTREIQ_HORIZONTAL_WHITE.svg'
    : '/WORDMARK_SPECTREIQ_HORIZONTAL_BLACK.svg'

  return (
    <div
      className={`
        min-h-screen flex flex-col
        ${isDark ? 'bg-[#050505] text-white' : 'bg-[#FAFAFA] text-gray-900'}
      `}
    >
      {/* Header */}
      <header className="flex items-center justify-between px-6 py-4 lg:px-12">
        <img
          src={wordmark}
          alt="SPECTREIQ"
          className="h-8 lg:h-10 object-contain"
        />
        <Button
          variant="ghost"
          size="icon"
          onClick={toggleTheme}
          className={`h-10 w-10 ${isDark ? 'text-white/70 hover:text-white' : 'text-gray-600 hover:text-gray-900'}`}
          aria-label={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
        >
          {isDark ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
        </Button>
      </header>

      {/* Main Content - Two Column Layout */}
      <main className="flex-1 flex flex-col lg:flex-row items-center justify-center px-6 lg:px-12 py-8 gap-8 lg:gap-16">

        {/* Left Column - Hero Content */}
        <div className="flex flex-col items-center lg:items-start text-center lg:text-left max-w-xl">
          {/* Headline */}
          <h1 className="text-4xl lg:text-5xl xl:text-6xl font-bold tracking-tight leading-tight mb-6">
            <span className={isDark ? 'text-white' : 'text-black'}>
              ACCELERATE YOUR MISSION
            </span>
            <br />
            <span className="text-[#e26e32]">
              FROM LAB TO FIELD
            </span>
          </h1>

          {/* Description */}
          <p className={`text-base lg:text-lg mb-8 max-w-md ${isDark ? 'text-white/70' : 'text-gray-800'}`}>
            The Engineering and Analytics Platform for High-Impact, No-Fail Missions.
          </p>

          {/* CTA Button - Direct auth trigger */}
          <Button
            onClick={login}
            className={`
              h-14 px-10 text-base font-bold uppercase tracking-widest
              rounded-none
              ${isDark
                ? 'bg-white text-black hover:bg-white/90'
                : 'bg-gray-900 text-white hover:bg-gray-800'
              }
            `}
          >
            <Rocket className="w-5 h-5 mr-3" />
            Launch
          </Button>

        </div>

        {/* Right Column - Architecture Diagram */}
        <div className="w-full lg:w-auto lg:flex-1 max-w-3xl">
          <SpectreArchitecture />
        </div>
      </main>

      {/* Footer */}
      <footer className={`px-6 py-4 lg:px-12 flex justify-center lg:justify-start ${isDark ? 'text-white/30' : 'text-gray-500'}`}>
        <div className="text-center lg:text-left">
          <p className="text-xs">
            &copy; 2026 AMPERSAND &bull; SPECTREIQ Platform
          </p>
        </div>
      </footer>
    </div>
  )
}
